import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAgenda,
  deleteAgenda,
  getAgenda,
  getAgendaCalendar,
  respondAgenda,
  updateAgenda,
} from '../api/agendaApi'
import type { AgendaRespond, AgendaScope, AgendaUpsert } from '../types'

const KEY = 'agendas'
const CALENDAR_KEY = [KEY, 'calendar'] as const
const detailKey = (id: number) => [KEY, 'detail', id] as const

/*
 * Optimistic güncelleme yok: gövde görüntüleyene göre değişir ve `editable`/`respondable` bayraklarını
 * yalnız sunucu hesaplar — her yazmadan sonra sorgular bayatlatılıp yeniden okunur.
 */

/** Haftalık takvim. Her yazma `agendas` önekini bayatlattığı için kendiliğinden tazelenir. */
export function useAgendaCalendar(from: string, to: string) {
  return useQuery({
    queryKey: [...CALENDAR_KEY, from, to],
    queryFn: () => getAgendaCalendar(from, to),
  })
}

/** Tek toplantı. `404` merkezde toast'lanmaz — detay ekranı kendi mesajıyla takvime döner. */
export function useAgenda(id: number) {
  return useQuery({
    queryKey: detailKey(id),
    queryFn: () => getAgenda(id),
    enabled: Number.isFinite(id) && id > 0,
    meta: { handledCodes: ['NOT_FOUND'] },
  })
}

/**
 * Oluşturma + güncelleme. `VALIDATION_ERROR` form alanına, `INVALID_STATE` (bitmiş toplantı) ekrana
 * düşer; optimistic lock (409) merkezde kalır.
 */
export function useSaveAgenda() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: [KEY] })

  const create = useMutation({
    mutationFn: (body: AgendaUpsert) => createAgenda(body),
    onSuccess: invalidate,
    meta: { handledCodes: ['VALIDATION_ERROR', 'INVALID_STATE'] },
  })

  // `scope` yalnız "Serinin tamamı"nda geçirilir: sunucu varsayılanı "yalnız bu toplantı"dır ve
  // parametresiz çağrı 125'teki istekle birebir aynı kalır.
  const update = useMutation({
    mutationFn: ({ id, body, scope }: { id: number; body: AgendaUpsert; scope?: AgendaScope }) =>
      scope === 'SERIES' ? updateAgenda(id, body, scope) : updateAgenda(id, body),
    onSuccess: invalidate,
    meta: { handledCodes: ['VALIDATION_ERROR', 'INVALID_STATE'] },
  })

  return { create, update }
}

/**
 * Silme. Yalnız takvim bayatlatılır: detayı bayatlatmak açık ekranda silinmiş kayıt için yeniden
 * istek atar ve kullanıcı kendi işleminden sonra "bulunamadı" uyarısı görürdü.
 */
export function useDeleteAgenda() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, scope }: { id: number; scope?: AgendaScope }) =>
      scope === 'SERIES' ? deleteAgenda(id, scope) : deleteAgenda(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: CALENDAR_KEY }),
    meta: { handledCodes: ['INVALID_STATE'] },
  })
}

/** Katılım yanıtı. Hatada da tazelenir (`onSettled`): 409 sonrası ekran güncel hâli gösterir. */
export function useRespondAgenda(id: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: AgendaRespond) => respondAgenda(id, body),
    onSettled: () => qc.invalidateQueries({ queryKey: [KEY] }),
    meta: { handledCodes: ['VALIDATION_ERROR', 'INVALID_STATE'] },
  })
}
